
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  const footerLinks = {
    product: [
      { name: t('proof_series_subtitle'), href: '#products' },
      { name: t('declaration_series_subtitle'), href: '#products' },
      { name: t('global_sourcing'), href: '#sourcing' },
      { name: t('quality_assurance'), href: '#' }
    ],
    company: [
      { name: t('about_us'), href: '#story' },
      { name: t('our_philosophy'), href: '#philosophy' },
      { name: t('contact_us'), href: '#' },
      { name: t('careers'), href: '#' }
    ],
    support: [
      { name: t('faq'), href: '#' },
      { name: t('user_guide'), href: '#' },
      { name: t('customer_service'), href: '#' },
      { name: t('return_policy'), href: '#' }
    ]
  };

  const socialLinks = [
    { icon: 'ri-wechat-line', href: '#', name: '微信' },
    { icon: 'ri-weibo-line', href: '#', name: '微博' },
    { icon: 'ri-instagram-line', href: '#', name: 'Instagram' },
    { icon: 'ri-xiaohongshu-line', href: '#', name: '小红书' }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#2A2A2A] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8 md:gap-12 mb-12">
          {/* Brand Section */}
          <div className="md:col-span-3 lg:col-span-1 order-1 md:order-1 lg:order-1">
            <h3 
              className="text-base sm:text-lg lg:text-lg font-bold mb-4"
              style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
            >
              LunaKey
            </h3>
            <p 
              className="text-xs leading-relaxed mb-6"
              style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
            >
              {t('footer_description')}
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-4 h-4 bg-[#F5F1E6]/20 rounded-full flex items-center justify-center hover:bg-[#F5F1E6]/40 transition-colors cursor-pointer"
                  aria-label={social.name}
                >
                  <i className={`${social.icon} text-xs`}></i>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Footer Links - Three columns in one row */}
        <div className="grid grid-cols-3 gap-4 mb-12 md:hidden">
          {/* Product Links */}
          <div>
            <h4 
              className="text-xs font-semibold mb-4"
              style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
            >
              {t('product_series')}
            </h4>
            <ul className="space-y-2">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-xs text-[#A6A6A6] hover:text-white transition-colors cursor-pointer text-left"
                    style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 
              className="text-xs font-semibold mb-4"
              style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
            >
              {t('about_us')}
            </h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-xs text-[#A6A6A6] hover:text-white transition-colors cursor-pointer text-left"
                    style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 
              className="text-xs font-semibold mb-4"
              style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
            >
              {t('customer_service')}
            </h4>
            <ul className="space-y-2">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-xs text-[#A6A6A6] hover:text-white transition-colors cursor-pointer"
                    style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Desktop Footer Links - Hidden on mobile */}
        <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-3 gap-8 md:gap-12 mb-12">
          {/* Product Links */}
          <div>
            <h4 
              className="text-xs lg:text-xs font-semibold mb-6"
              style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
            >
              {t('product_series')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-xs text-[#A6A6A6] hover:text-white transition-colors cursor-pointer text-left"
                    style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 
              className="text-xs lg:text-xs font-semibold mb-6"
              style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
            >
              {t('about_us')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-xs text-[#A6A6A6] hover:text-white transition-colors cursor-pointer text-left"
                    style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 
              className="text-xs lg:text-xs font-semibold mb-6"
              style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
            >
              {t('customer_service')}
            </h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-xs text-[#A6A6A6] hover:text-white transition-colors cursor-pointer"
                    style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-[#A6A6A6]/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p 
              className="text-[#A6A6A6] text-xs"
              style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
            >
              © {currentYear} LunaKey. {t('all_rights_reserved')}。
            </p>
            
            <div className="flex items-center space-x-6">
              <a
                href="#"
                className="text-[#A6A6A6] hover:text-white transition-colors text-xs cursor-pointer"
                style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
              >
                {t('privacy_policy')}
              </a>
              <a
                href="#"
                className="text-[#A6A6A6] hover:text-white transition-colors text-xs cursor-pointer"
                style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
              >
                {t('terms_of_service')}
              </a>
              <a
                href="https://readdy.ai/?origin=logo"
                className="text-[#A6A6A6] hover:text-white transition-colors text-xs cursor-pointer"
                style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
              >
                Website Builder
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
